import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { APPT_ENUMS, FindLoad, Images, renderClientName, useModal } from 'utils';
import { appointmentActions, systemActions } from 'store';
import { ServiceItems } from './serviceItems';
import { OtherItems } from './otherItems';
import { SignatureDetails } from './signatureDetails';
import { ApptAccounting } from './apptAccounting';
import { billStatuses, scheduleStatuses, SERVICE, TELEHEALTH_PROVIDERS } from '../../../constants';
import { CustomDelete, EditWithIcon, MinLoader, NotError } from 'components';
import { systemService } from '../../../../../store/system/system.service';

const ZOOM_ACTION_TYPE = 'CREATE_ZOOM_MEETING';
const ZOOM_DELETE_ACTION_TYPE = 'DELETE_ZOOM_MEETING';

const actionEnums = {
    OPEN_LINK: 'OPEN_LINK',
    COPY_LINK: 'COPY_LINK',
    REMOVE_LINK: 'REMOVE_LINK',
};
export const Details = ({ item, currentId }) => {
    const zoomConnection = useSelector((state) => state.system.zoomConnection);
    const googleMeetConnection = useSelector((state) => state.system.googleMeetConnection);
    const dispatch = useDispatch();
    const checkAccounting = item?.billStatus === billStatuses?.BILLED || item?.status === scheduleStatuses?.COMPLETED;
    const [load, setLoad] = useState('');
    const loadCreate = FindLoad(ZOOM_ACTION_TYPE);
    const { open } = useModal();
    const [activeSession, setActiveSession] = useState(undefined);
    const activeProvider = googleMeetConnection?.isConnected ? TELEHEALTH_PROVIDERS.GOOGLE_MEET : zoomConnection?.isConnected ? TELEHEALTH_PROVIDERS.ZOOM : null;


    useEffect(() => {
        if (checkAccounting && item?.type === SERVICE) {
            dispatch(appointmentActions.getAppointmentInfo(currentId));
        }
    }, [item]);

    useEffect(() => {
        if (!item?.id || !item?.telehealth || item?.type !== APPT_ENUMS.SERVICE) return;
        if (!activeProvider) return;
        if (!item?.staff?.telehealth) return;
        if (activeProvider === TELEHEALTH_PROVIDERS.ZOOM && !item?.staff?.zoomUserId) return;

        let cancelled = false;
        systemService.getActiveSessionService(item.id)
            .then((res) => {
                if (!cancelled) setActiveSession(res?.data || null);
            })
            .catch(() => {
                if (!cancelled) setActiveSession(null);
            });

        return () => {
            cancelled = true;
        };
    }, [item, activeProvider]);

    const handleMeetingEnded = () => {
        setActiveSession(null);
        toast(<NotError type={'error'} errMessage={'Meeting has ended, please create new one'} />);
    };

    const handleAction = async (type) => {
        setLoad(type);

        if (type === actionEnums.OPEN_LINK) {
            systemService
                .getMeetLink(activeSession?._id)
                .then((res) => {
                    window.open(res?.data, '_blank', 'noopener,noreferrer');
                })
                .catch((error) => {
                    if (error?.status === 410) {
                        handleMeetingEnded();
                    } else {
                        toast(<NotError type={'error'} errMessage={error?.data?.message} />);
                    }
                })
                .finally(() => setLoad(''));
        }

        if (type === actionEnums.COPY_LINK) {
            if (activeSession?.joinUrl) {
                navigator.clipboard.writeText(activeSession.joinUrl).then(() => toast('Link copied to clipboard'));
            }
            setLoad('');
        }
    };

    const createMeeting = (provider) => {
        dispatch(systemActions.createZoomMeeting({
            'apptId': item?.id,
            'staffId': item?.staff?.id,
            'staffFullName': item?.staff?.fullName,
            'clientFullName': renderClientName(item?.client),
            'provider': provider,
        }, item));
    };

    return (
        <div>
            <div className="detail-information">
                {item?.type === APPT_ENUMS.SERVICE ? (
                    <div>
                        <ServiceItems item={item} />
                        {(item?.signature?.key || item?.providerSignature?.key) && <SignatureDetails item={item} />}

                        {checkAccounting && (
                            <ApptAccounting afterSuccess={() => dispatch(appointmentActions.getAppointmentInfo(currentId))} />
                        )}
                    </div>
                ) : (
                    <OtherItems item={item} />
                )}
            </div>

            {item?.type === APPT_ENUMS.SERVICE && item?.telehealth && (
                <div style={{ width: '49%' }}>
                    <div className="full-width" style={{ marginTop: 24 }}>
                        <div className="space-between" style={{ marginBottom: 12 }}>
                            <p className="attached-link-body-title">Video Meeting</p>
                        </div>

                        <div className="link-line-box">
                            {(() => {
                                /* State 1: No telehealth provider connected */
                                if (!activeProvider) {
                                    return (
                                        <>
                                            <div className="flex-align-center" style={{ gap: 12 }}>
                                                <img src={Images.infoCircle} alt="info" />
                                                <p className="zoom-status-text">
                                                    Video meeting unavailable - No telehealth provider connected.
                                                </p>
                                            </div>
                                            <button disabled className="zoom-action-btn" style={{ width: 160, opacity: 0.5 }}>
                                                Add Meeting
                                            </button>
                                        </>
                                    );
                                }

                                const providerLabel = activeProvider === TELEHEALTH_PROVIDERS.GOOGLE_MEET ? 'Google Meet' : 'Zoom';

                                /* State 2: Telehealth disabled for staff */
                                if (!item?.staff?.telehealth) {
                                    return (
                                        <>
                                            <div className="flex-align-center" style={{ gap: 12 }}>
                                                <img src={Images.infoCircle} alt="info" />
                                                <p className="zoom-status-text">
                                                    Video meeting unavailable - Telehealth is disabled for this staff.
                                                </p>
                                            </div>
                                            <button disabled className="zoom-action-btn" style={{ width: 200, opacity: 0.5 }}>
                                                Add {providerLabel} Meeting
                                            </button>
                                        </>
                                    );
                                }

                                /* State 3 (Zoom only): No Zoom account assigned to staff */
                                if (activeProvider === TELEHEALTH_PROVIDERS.ZOOM && !item?.staff?.zoomUserId) {
                                    return (
                                        <>
                                            <div className="flex-align-center" style={{ gap: 12 }}>
                                                <img src={Images.infoCircle} alt="info" />
                                                <p className="zoom-status-text">
                                                    Zoom meeting unavailable - No Zoom account assigned to this staff.
                                                </p>
                                            </div>
                                            <button disabled className="zoom-action-btn" style={{ width: 160, opacity: 0.5 }}>
                                                Add Zoom Meeting
                                            </button>
                                        </>
                                    );
                                }

                                /* Loading: checking for active session */
                                if (activeSession === undefined) {
                                    return <MinLoader position={'relative'} margin={'0'} color={'#414651'} />;
                                }

                                /* State 4: Meeting exists */
                                if (activeSession) {
                                    const sessionLabel = activeSession?.provider === TELEHEALTH_PROVIDERS.GOOGLE_MEET ? 'Google Meet' : 'Zoom';
                                    return (
                                        <>
                                            <div className="flex-align-center" style={{ gap: 12 }}>
                                                <img src={Images.linkIcon} alt="icon" />
                                                <p>{sessionLabel}</p>
                                            </div>
                                            <div className="flex-align-center" style={{ gap: 8 }}>
                                                <EditWithIcon
                                                    icon={Images.externalLink}
                                                    handleClick={() => handleAction(actionEnums.OPEN_LINK)}
                                                    load={load === actionEnums.OPEN_LINK}
                                                />
                                                <EditWithIcon
                                                    icon={Images.copy}
                                                    handleClick={() => handleAction(actionEnums.COPY_LINK)}
                                                    load={load === actionEnums.COPY_LINK}
                                                />
                                                <EditWithIcon
                                                    icon={Images.redTrash}
                                                    handleClick={() => open(
                                                        <CustomDelete
                                                            text="Are you sure you want to delete the link?"
                                                            info="Delete Link?"
                                                            handleDel={() => dispatch(systemActions.deleteZoomMeeting(item?.id, item))}
                                                            actionType={ZOOM_DELETE_ACTION_TYPE}
                                                            innerText={'Delete'}
                                                            body={
                                                                <div className="deleting-client-info-box" style={{ alignItems: 'flex-start' }}>
                                                                    <img style={{ marginTop: '3px' }} src={Images.externalLink} alt="icon" />
                                                                    <p style={{ fontWeight: 400 }}>{sessionLabel}</p>
                                                                </div>
                                                            }
                                                        />,
                                                    )}
                                                />
                                            </div>
                                        </>
                                    );
                                }

                                /* State 5: Ready to add meeting */
                                return (
                                    <>
                                        <div className="flex-align-center" style={{ gap: 12 }}>
                                            <img src={Images.linkIcon} alt="icon" />
                                            <p className="zoom-status-text">No {providerLabel} meeting added</p>
                                        </div>
                                        <button
                                            disabled={!!loadCreate?.length}
                                            style={{ width: 200 }}
                                            onClick={() => createMeeting(activeProvider)}
                                            className="zoom-action-btn"
                                        >
                                            {loadCreate?.length ? (
                                                <MinLoader position={'relative'} margin={'0'} color={'#414651'} />
                                            ) : (
                                                `Add ${providerLabel} Meeting`
                                            )}
                                        </button>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
